import { Autocomplete, Stack, TextField, Typography } from '@mui/material'
import { useEffect, useState } from 'react'

import { matches } from '@/lib/match'

/** Labels and help, matching `domain/asks.py`. */
const LABELS: Record<string, string> = {
  allergies: "Anything you can't eat?",
  foods: 'What would you like to eat?',
  songs: 'A song for the night',
}

const HELP: Record<string, string> = {
  allergies:
    "Anything anyone in your party can't eat. Only the organiser sees this.",
  foods: 'One dish. Others can see what has been asked for.',
  songs: 'To play, or to sing.',
}

interface Props {
  asks: string[]
  answers: Record<string, string>
  /** What other guests typed, per ask. Never present for allergies. */
  suggestions: Record<string, string[]>
  /** The fixed allergen vocabulary, when this invite asks about allergies. */
  allergens: string[]
  busy?: boolean
  disabled?: boolean
  onSave: (answers: Record<string, string>) => void
}

/**
 * The questions an invite asks after the answer that matters.
 *
 * Below the yes, never in front of it. The RSVP is the thing being chased and
 * three extra fields ahead of it would cost answers; somebody who has already
 * said they are coming has nothing left to lose by mentioning a song.
 *
 * **Where the options come from is not cosmetic.** Foods and songs complete
 * from what other guests have typed, which is how a party converges on one
 * spelling of biryani and how the count underneath comes to mean anything.
 * Allergies complete from a FIXED list instead — offering "peanuts" because
 * another guest is allergic would disclose a medical fact about a named
 * person to everybody holding an invite link. The server enforces that
 * asymmetry; this component only has to not undo it, which it does by asking
 * for the two lists separately rather than merging them.
 *
 * Saved on the way out of each field, the same as the profile screen. An
 * explicit Save under three optional fields is a button most people would
 * leave unpressed, and losing a song to a closed tab is worse than a write
 * nobody asked for.
 */
export function AskFields({
  asks,
  answers,
  suggestions,
  allergens,
  busy,
  disabled,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<Record<string, string>>(answers)

  // Follow the server when it disagrees — another tab, or a value it
  // trimmed. Keyed on the answers themselves rather than on a render, so
  // typing is never interrupted by a refetch that changed nothing.
  useEffect(() => setDraft(answers), [answers])

  if (!asks.length) return null

  const commit = (kind: string, value: string) => {
    const next = { ...draft, [kind]: value }
    setDraft(next)
    // Only when it actually differs. A blur with nothing typed is the
    // commonest interaction on an optional field, and it should be free.
    if ((answers[kind] ?? '') !== value) onSave(next)
  }

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      <Typography variant="body2" sx={{ fontWeight: 620 }}>
        A few things, while you are here
      </Typography>

      {asks.map((kind) => {
        const options =
          kind === 'allergies' ? allergens : (suggestions[kind] ?? [])
        return (
          <Autocomplete
            key={kind}
            freeSolo
            disabled={disabled || busy}
            options={options}
            value={draft[kind] ?? ''}
            // Both handlers, because they fire for different acts: picking an
            // option from the list, and typing without picking one. Only the
            // first is a `value` change, and an invite that recorded only
            // those would keep nothing anybody typed themselves.
            onChange={(_e, picked) => commit(kind, (picked as string) ?? '')}
            onInputChange={(_e, typed) =>
              setDraft((was) => ({ ...was, [kind]: typed }))
            }
            onBlur={() => commit(kind, draft[kind] ?? '')}
            filterOptions={(all, state) => matches(all, state.inputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={LABELS[kind] ?? kind}
                helperText={HELP[kind] ?? ''}
                slotProps={{ htmlInput: { ...params.inputProps, maxLength: 200 } }}
              />
            )}
          />
        )
      })}
    </Stack>
  )
}
